/**
 * /well-testing/wells/:id — one well
 * (GET / PATCH / DELETE /well-testing/wells/:id, fieldiq-api#1241).
 *
 * Panels stack full width: details on top, the edit form below them when
 * editing (never a modal).
 *
 * Edit is WT_WELL_WRITE (admin, office_staff, supervisor); delete is WT_ADMIN
 * (admin, office_staff) and asks in-app first. Any other role reads. A
 * refusal that still arrives is shown as a refusal in the form, never as a
 * green toast; a 409 duplicate_well names the existing well and links to it.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, Droplet, MapPin, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useDeleteWtWell, useUpdateWtWell, useWtCustomers, useWtMeta, useWtWell } from '../../hooks/useWellTesting';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canAdminWt,
  canWriteWells,
  catalogLabel,
  duplicateWell,
  fmtWtNumber,
  wellIdLabel,
  wellIdOf,
  withUnit,
  wtErrorMessage,
  wtRefusal,
} from '../../lib/well-testing';
import { DH_BAND_SECONDARY } from '../downhole/DownholeBits';
import { useBackToList } from '../downhole/recordPage';
import { WellForm } from './WellForm';
import { WellJobsSection } from './WellJobsSection';
import { isNotFound, WT_EYEBROW, WT_PRIMARY, WT_SECONDARY, WT_SOUR_SKIN, WtBadge, WtDialog, WtEmpty, WtFormError, WtLoading, WtPanel, WtSpec } from './WtBits';

export default function WtWellDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const back = useBackToList('/well-testing/wells');
  const { user } = useAuth();
  const query = useWtWell(id);
  const catalog = useWtMeta().data?.data ?? null;
  const canEdit = canWriteWells(user?.role);
  const canDelete = canAdminWt(user?.role);
  const customers = useWtCustomers(canEdit);
  const update = useUpdateWtWell();
  const del = useDeleteWtWell();
  const [editing, setEditing] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [duplicateOf, setDuplicateOf] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // The duplicate link opens another well on this same route: start it closed.
  useEffect(() => {
    setEditing(false);
    setEditError(null);
    setDuplicateOf(null);
    setDeleting(false);
  }, [id]);

  const well = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const failure = queryLoadFailure(query);
  const notFound = isNotFound(queryFailureError(query));
  const fluid = well ? well.fluidTypeLabel ?? catalogLabel(catalog?.fluidTypes, well.fluidType) : '—';
  const idLabel = wellIdLabel(meta);

  return (
    <div data-testid="wt-well-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={WT_EYEBROW}
        title={well?.wellName || (query.isLoading ? 'Well' : '—')}
        onBack={back}
        backLabel="Back"
        actions={
          well && (canEdit || canDelete) ? (
            <div className="flex flex-wrap gap-2">
              {canEdit && (
                <button
                  type="button"
                  className="fi-action fi-action--standard fi-action--primary"
                  onClick={() => {
                    setEditError(null);
                    setDuplicateOf(null);
                    setEditing(true);
                  }}
                  disabled={editing}
                >
                  <Pencil className="size-4" aria-hidden="true" />
                  Edit well
                </button>
              )}
              {canDelete && (
                <button type="button" className={DH_BAND_SECONDARY} onClick={() => { setDeleteError(null); setDeleting(true); }}>
                  <Trash2 className="size-4" aria-hidden="true" />
                  Delete
                </button>
              )}
            </div>
          ) : undefined
        }
        stats={[
          { icon: <Droplet className="size-4" aria-hidden="true" />, label: 'Fluid', tone: 'neutral', loading: query.isLoading, value: fluid },
          {
            icon: <AlertTriangle className="size-4" aria-hidden="true" />,
            label: 'Service',
            tone: well?.sour ? 'accent' : 'neutral',
            loading: query.isLoading,
            value: well ? (well.sour ? 'Sour' : 'Sweet') : '—',
          },
          { icon: <MapPin className="size-4" aria-hidden="true" />, label: 'Region', tone: 'neutral', loading: query.isLoading, value: well?.provinceState || '—' },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <WtLoading label="Loading well" />
          ) : notFound || (!failure && !well) ? (
            <WtPanel eyebrow="Well" title="Well">
              <WtEmpty>This well was not found. It may have been deleted.</WtEmpty>
            </WtPanel>
          ) : failure || !well ? (
            <WtPanel eyebrow="Well" title="Well">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this well" />
            </WtPanel>
          ) : (
            <>
              <WtPanel eyebrow="Well" title="Details" testId="wt-well-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <WtSpec label="Customer" value={well.customerName || '—'} />
                  <WtSpec label={idLabel} value={wellIdOf(well, meta) || '—'} mono />
                  {/* The other country's identifier, only when this well holds one. */}
                  {idLabel === 'UWI' && well.apiNumber && <WtSpec label="API number" value={well.apiNumber} mono />}
                  {idLabel === 'API number' && well.uwi && <WtSpec label="UWI" value={well.uwi} mono />}
                  <WtSpec label="Licence number" value={well.licenceNumber || '—'} mono />
                  <WtSpec label="Surface location" value={well.surfaceLocation || '—'} />
                  <WtSpec label="Province / state" value={well.provinceName || well.provinceState || '—'} />
                  <WtSpec
                    label="Coordinates"
                    value={well.latitude != null && well.longitude != null ? `${fmtWtNumber(well.latitude)}, ${fmtWtNumber(well.longitude)}` : '—'}
                    mono
                  />
                  <WtSpec label="Field" value={well.fieldName || '—'} />
                  <WtSpec label="Pool" value={well.poolName || '—'} />
                  <WtSpec label="Fluid" value={fluid} />
                  <WtSpec label="Service" value={well.sour ? <WtBadge skin={WT_SOUR_SKIN} value="sour" /> : 'Sweet'} />
                  <WtSpec label="Expected H2S" value={withUnit(well.expectedH2sPpm, 'ppm')} mono />
                </dl>
                {well.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{well.notes}</p>}
              </WtPanel>

              <WellJobsSection wellId={well.id} />

              {editing && canEdit && (
                <WtPanel eyebrow="Edit" title="Edit well" testId="wt-well-edit">
                  <WellForm
                    key={well.id}
                    well={well}
                    catalog={catalog}
                    meta={meta}
                    customers={customers.data ?? []}
                    pending={update.isPending}
                    error={editError}
                    duplicateOf={duplicateOf}
                    submitLabel="Save well"
                    onCancel={() => setEditing(false)}
                    onSubmit={(body) => {
                      setEditError(null);
                      setDuplicateOf(null);
                      update.mutate(
                        { id: well.id, body },
                        {
                          onSuccess: () => {
                            toast.success('Well saved');
                            setEditing(false);
                          },
                          onError: (err) => {
                            const dup = duplicateWell(err);
                            setEditError(dup ? dup.message : wtErrorMessage(err, 'The well could not be saved.'));
                            setDuplicateOf(dup?.existingWellId ?? null);
                          },
                        },
                      );
                    }}
                  />
                </WtPanel>
              )}
            </>
          )}
        </div>
      </section>

      {well && canDelete && (
        <WtDialog
          open={deleting}
          title="Delete well"
          onClose={() => setDeleting(false)}
          footer={
            <>
              <button type="button" className={WT_SECONDARY} onClick={() => setDeleting(false)} disabled={del.isPending}>
                Keep well
              </button>
              <button
                type="button"
                className={WT_PRIMARY}
                disabled={del.isPending}
                onClick={() =>
                  del.mutate(well.id, {
                    onSuccess: () => {
                      toast.success('Well deleted');
                      navigate('/well-testing/wells', { replace: true });
                    },
                    onError: (err) => setDeleteError(wtRefusal(err, 'The well could not be deleted.')),
                  })
                }
              >
                {del.isPending ? 'Deleting…' : 'Delete well'}
              </button>
            </>
          }
        >
          <WtFormError message={deleteError} />
          <p className="text-sm text-fieldiq-text">
            Removes {well.wellName} from the register.
            {wellIdOf(well, meta) ? ` Its ${idLabel} is freed for another well.` : ''}
          </p>
        </WtDialog>
      )}
    </div>
  );
}
